import {
  Component,
  computed,
  effect,
  inject,
  signal,
  viewChild,
  DestroyRef,
} from "@angular/core";
import { HttpContext } from "@angular/common/http";
import { CommonModule } from "@angular/common";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import {
  O2SheetImports,
  O2SheetComponent,
  HlmButtonImports,
  O2AccordionImports,
  HlmLabelImports,
  HlmInput,
  HlmFormFieldImports,
  HlmInputGroupImports,
  ButtonGroupComponent,
  HlmIconImports,
  HlmSelectImports,
  BrnSelectImports,
  HlmTooltipImports,
  HlmAlertImports,
} from "@o2-fe/ui";
import {
  LoggedUserService,
  TenantFormValues,
  SprintDuration,
} from "@o2-fe/core";
import { toSignal, takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UsersTenantsService } from "@o2-fe/api";
import { ToastService, SUPPRESS_SUCCESS_TOAST } from "@o2-fe/toast-message";
import { map, switchMap } from "rxjs";
import { mapTenantFormToUpdateDto } from "../../mappers/tenant-form.mapper";
import { NgIcon, provideIcons } from "@ng-icons/core";
import {
  lucideInfo,
  lucideCircleCheck,
  lucideFileText,
  lucideUpload,
  lucideTrash2,
  lucideTriangleAlert,
} from "@ng-icons/lucide";
import { CustomTemplateService } from "../../../shared/services/custom-template.service";
import {
  CustomTemplateType,
  CustomTemplateValidationFailure,
  TemplatePlaceholderMetadata,
} from "../../../shared/interfaces/custom-template.interface";
import { TemplateValidationErrorsDialogComponent } from "./template-validation-errors-dialog/template-validation-errors-dialog.component";
import { TemplateValidationWarningsDialogComponent } from "./template-validation-warnings-dialog/template-validation-warnings-dialog.component";
import { TemplateRemoveConfirmDialogComponent } from "./template-remove-confirm-dialog/template-remove-confirm-dialog.component";

/** Maps each template type to its corresponding tenant S3 key field. */
const TEMPLATE_S3_KEY_FIELD: Record<
  CustomTemplateType,
  "quoteTemplateS3Key" | "contractTemplateS3Key"
> = {
  quote: "quoteTemplateS3Key",
  contract: "contractTemplateS3Key",
};

/**
 * Maps each template type to the tenant field holding the S3 key of the
 * auto-translated English version of the template.
 */
const TEMPLATE_EN_S3_KEY_FIELD: Record<
  CustomTemplateType,
  "quoteTemplateEnS3Key" | "contractTemplateEnS3Key"
> = {
  quote: "quoteTemplateEnS3Key",
  contract: "contractTemplateEnS3Key",
};

/** Maps each template type to its static example file and download name. */
const TEMPLATE_EXAMPLE_FILE: Record<
  CustomTemplateType,
  { fileName: string; downloadName: string }
> = {
  quote: {
    fileName: "quote-template-example.docx",
    downloadName: "template-preventivo-esempio.docx",
  },
  contract: {
    fileName: "contract-template-example.docx",
    downloadName: "template-contratto-esempio.docx",
  },
};

/** Inclusive lower bound for a project-budget alert threshold (percentage). */
const BUDGET_THRESHOLD_MIN = 1;
/** Inclusive upper bound for a project-budget alert threshold (percentage). */
const BUDGET_THRESHOLD_MAX = 100;

/**
 * Validator rejecting non-integer values: budget thresholds are whole
 * percentages. Fractional thresholds could fall inside an integer band and
 * never be evaluated by the backend crossing check (which advances by whole
 * percentage points). `null`/empty is left to the optional rules.
 */
const integerValidator: ValidatorFn = (control) =>
  control.value == null || Number.isInteger(control.value)
    ? null
    : { integer: true };

/** Shared validators for the four budget-threshold controls. */
const budgetThresholdValidators = [
  Validators.min(BUDGET_THRESHOLD_MIN),
  Validators.max(BUDGET_THRESHOLD_MAX),
  integerValidator,
];

@Component({
  selector: "app-tenant-config-sheet",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    O2SheetImports,
    HlmButtonImports,
    O2AccordionImports,
    HlmLabelImports,
    HlmInput,
    HlmFormFieldImports,
    HlmInputGroupImports,
    ButtonGroupComponent,
    HlmIconImports,
    NgIcon,
    HlmSelectImports,
    BrnSelectImports,
    HlmTooltipImports,
    HlmAlertImports,
    TemplateValidationErrorsDialogComponent,
    TemplateValidationWarningsDialogComponent,
    TemplateRemoveConfirmDialogComponent,
  ],
  providers: [
    provideIcons({
      lucideInfo,
      lucideCircleCheck,
      lucideFileText,
      lucideUpload,
      lucideTrash2,
      lucideTriangleAlert,
    }),
  ],
  templateUrl: "./tenant-config-sheet.component.html",
})
export class TenantConfigSheetComponent {
  private readonly _loggedUserService = inject(LoggedUserService);
  private readonly _tenantsService = inject(UsersTenantsService);
  private readonly _customTemplateService = inject(CustomTemplateService);
  private readonly _toastService = inject(ToastService);
  private readonly _destroyRef = inject(DestroyRef);

  // ViewChild for the sheet
  protected readonly sheetRef = viewChild.required(O2SheetComponent);

  // ViewChild for the custom template validation errors dialog (syntax errors)
  protected readonly templateValidationDialogRef = viewChild(
    TemplateValidationErrorsDialogComponent,
  );

  // ViewChild for the custom template validation warnings dialog (missing placeholders)
  protected readonly templateValidationWarningsDialogRef = viewChild(
    TemplateValidationWarningsDialogComponent,
  );

  // ViewChild for the remove confirmation dialog
  protected readonly templateRemoveConfirmDialogRef = viewChild(
    TemplateRemoveConfirmDialogComponent,
  );

  /**
   * Pending upload pinned during the warning dialog interaction. When
   * validation surfaces missing placeholders we show the warning and pause
   * the flow; if the user clicks "Carica comunque" we resume from this state.
   */
  private readonly _pendingUpload = signal<{
    file: File;
    templateType: CustomTemplateType;
  } | null>(null);

  /** Template type pending removal (set before the confirm dialog opens). */
  private readonly _pendingRemoveType = signal<CustomTemplateType | null>(null);

  // Get current tenant
  protected readonly tenant = toSignal(this._loggedUserService.tenant$);

  // Compute sheet title
  protected readonly sheetTitle = computed(() => {
    const currentTenant = this.tenant();
    if (!currentTenant?.companyName) return "Profilo Azienda";
    return `Profilo ${currentTenant.companyName}`;
  });

  // Tenant form
  protected readonly tenantForm = new FormGroup({
    companyName: new FormControl<string>("", [
      Validators.required,
      Validators.minLength(2),
    ]),
    street: new FormControl<string>(""),
    city: new FormControl<string>(""),
    province: new FormControl<string>(""),
    postalCode: new FormControl<string>(""),
    email: new FormControl<string>("", [Validators.email]),
    phone: new FormControl<string>("", [
      Validators.pattern(/^[+]?[\d\s\-()]+$/),
    ]),
    sdi: new FormControl<string>(""),
    vatNumber: new FormControl<string>(""),
    indirectCosts: new FormControl<number | null>(null, [Validators.min(0)]),
    margin: new FormControl<number | null>(null, [Validators.min(0)]),
    billableHourlyRate: new FormControl<number | null>(null, [
      Validators.min(0),
    ]),
    inarcassaContribution: new FormControl<boolean>(false),
    sprint: new FormControl<SprintDuration | null>(null),
    insuranceCompany: new FormControl<string>(""),
    policyNumber: new FormControl<string>(""),
    budgetThreshold1: new FormControl<number | null>(
      null,
      budgetThresholdValidators,
    ),
    budgetThreshold2: new FormControl<number | null>(
      null,
      budgetThresholdValidators,
    ),
    budgetThreshold3: new FormControl<number | null>(
      null,
      budgetThresholdValidators,
    ),
    budgetThreshold4: new FormControl<number | null>(
      null,
      budgetThresholdValidators,
    ),
  });

  protected readonly errorMessage = signal<string | undefined>(undefined);
  protected readonly hasError = computed(() => Boolean(this.errorMessage()));

  // Track initial form values for change detection
  private readonly _initialFormValues = signal<TenantFormValues | null>(null);

  // Convert form valueChanges to signal for reactivity
  private readonly _formValue = toSignal(this.tenantForm.valueChanges, {
    initialValue: this.tenantForm.getRawValue(),
  });

  // Track form status changes
  private readonly _formStatus = toSignal(this.tenantForm.statusChanges, {
    initialValue: this.tenantForm.status,
  });

  // Options for inarcassa contribution button group
  protected readonly inarcassaOptions = [
    { label: "Sì", value: true },
    { label: "No", value: false },
  ];

  // Options for sprint duration select
  protected readonly sprintDurationOptions = [
    { label: "Settimanale", value: SprintDuration.WEEKLY },
    { label: "Bisettimanale", value: SprintDuration.BIWEEKLY },
    { label: "Mensile", value: SprintDuration.MONTHLY },
  ];

  // Budget-threshold input slots (control name + accessible label), rendered
  // in a loop so the four identical fields are declared once.
  protected readonly budgetThresholdFields = [
    { control: "budgetThreshold1", label: "Soglia budget 1" },
    { control: "budgetThreshold2", label: "Soglia budget 2" },
    { control: "budgetThreshold3", label: "Soglia budget 3" },
    { control: "budgetThreshold4", label: "Soglia budget 4" },
  ] as const;

  // Threshold bounds exposed to the template (single source of truth).
  protected readonly budgetThresholdMin = BUDGET_THRESHOLD_MIN;
  protected readonly budgetThresholdMax = BUDGET_THRESHOLD_MAX;

  // Computed signal to check if form has changes
  protected readonly hasChanges = computed(() => {
    const initial = this._initialFormValues();
    if (!initial) return false;

    // Trigger reactivity by accessing the signal
    const current = this._formValue();
    this._formStatus(); // Trigger reactivity

    // Compare form values
    return JSON.stringify(current) !== JSON.stringify(initial);
  });

  // Computed signal to check if save button should be disabled
  protected readonly isSaveDisabled = computed(() => {
    const hasChanges = this.hasChanges();

    // If no changes, disable save button
    if (!hasChanges) {
      return true;
    }

    // If form is invalid, disable save button
    if (this.tenantForm.invalid) {
      return true;
    }

    return false;
  });

  constructor() {
    // Effect to cleanup when sheet closes
    effect(() => {
      const isOpen = this.sheetRef().isOpen();

      // When sheet closes, reset forms and errors
      if (!isOpen) {
        this.tenantForm.reset();
        this.errorMessage.set(undefined);
        this._initialFormValues.set(null);
      }
    });
  }

  /**
   * Opens the sheet and loads tenant data
   */
  open(): void {
    const currentTenant = this.tenant();
    if (currentTenant) {
      this.tenantForm.patchValue({
        companyName: currentTenant.companyName || "",
        street: currentTenant.address?.street || "",
        city: currentTenant.address?.city || "",
        province: currentTenant.address?.province || "",
        postalCode: currentTenant.address?.postalCode || "",
        email: currentTenant.email || "",
        phone: currentTenant.phone || "",
        sdi: currentTenant.sdi || "",
        vatNumber: currentTenant.vatNumber || "",
        indirectCosts: currentTenant.indirectCosts ?? null,
        margin: currentTenant.margin ?? null,
        billableHourlyRate: currentTenant.billableHourlyRate ?? null,
        inarcassaContribution: currentTenant.inarcassaContribution ?? false,
        sprint: currentTenant.sprint,
        insuranceCompany: currentTenant.insuranceCompany || "",
        policyNumber: currentTenant.policyNumber || "",
        budgetThreshold1: currentTenant.budgetThresholds?.[0] ?? null,
        budgetThreshold2: currentTenant.budgetThresholds?.[1] ?? null,
        budgetThreshold3: currentTenant.budgetThresholds?.[2] ?? null,
        budgetThreshold4: currentTenant.budgetThresholds?.[3] ?? null,
      });

      // Store initial values for change detection
      this._initialFormValues.set(this.tenantForm.getRawValue());
    }
    this.sheetRef().open();
  }

  /**
   * Closes the sheet
   * Cleanup is handled automatically by the effect watching isOpen signal
   */
  close(): void {
    this.sheetRef().close();
  }

  /**
   * Save changes
   */
  protected save(): void {
    // Validate form
    if (this.tenantForm.invalid) {
      this.tenantForm.markAllAsTouched();
      return;
    }

    this.errorMessage.set(undefined);

    const currentTenant = this.tenant();
    if (!currentTenant?.name) {
      this.errorMessage.set("Tenant non trovato");
      return;
    }

    const formValues = this.tenantForm.getRawValue();
    const updateDto = mapTenantFormToUpdateDto(formValues);

    this._tenantsService
      .tenantsControllerUpdate({
        id: currentTenant.id,
        body: updateDto,
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (updatedTenant) => {
          // Update logged user service with the latest tenant data
          this._loggedUserService.updateTenant(updatedTenant);
          // Close sheet
          this.close();
        },
        error: () =>
          this.errorMessage.set("Errore durante il salvataggio. Riprova."),
      });
  }

  // ==========================================================================
  // Custom document templates — quote
  // ==========================================================================

  /**
   * Opens a file picker for uploading a custom quote template.
   */
  protected triggerQuoteTemplateUpload(): void {
    this._triggerTemplateUpload("quote");
  }

  /**
   * Opens the confirmation dialog before removing the quote template.
   */
  protected confirmRemoveQuoteTemplate(): void {
    this._pendingRemoveType.set("quote");
    this.templateRemoveConfirmDialogRef()?.open("quote");
  }

  /**
   * Downloads the static quote template example from /assets via a DOM anchor.
   */
  protected downloadQuoteTemplateExample(): void {
    this._downloadTemplateExample("quote");
  }

  /**
   * Generic handler used by the validation errors dialog when the user
   * clicks "Scarica esempio" inside the dialog itself. Routes to the correct
   * static file based on the template type the dialog was opened for.
   */
  protected downloadTemplateExample(type: CustomTemplateType): void {
    this._downloadTemplateExample(type);
  }


  /**
   * Listener for the warning dialog "Carica comunque" CTA: resumes the upload
   * pipeline from the pending file/type captured before the dialog opened.
   */
  protected onWarningConfirmed(): void {
    const pending = this._pendingUpload();
    if (pending) {
      this._performUpload(pending.file, pending.templateType);
      this._pendingUpload.set(null);
    }
  }

  /**
   * Listener for the warning dialog "Annulla" CTA: drops the pending upload.
   */
  protected onWarningCancelled(): void {
    this._pendingUpload.set(null);
  }

  /**
   * Listener for the remove confirmation dialog "Elimina" CTA:
   * proceeds with the actual template removal.
   */
  protected onRemoveConfirmed(): void {
    const type = this._pendingRemoveType();
    if (type) {
      this._removeTemplate(type);
      this._pendingRemoveType.set(null);
    }
  }

  // ==========================================================================
  // Custom document templates — contract
  // ==========================================================================

  /**
   * Opens a file picker for uploading a custom contract template.
   */
  protected triggerContractTemplateUpload(): void {
    this._triggerTemplateUpload("contract");
  }

  /**
   * Opens the confirmation dialog before removing the contract template.
   */
  protected confirmRemoveContractTemplate(): void {
    this._pendingRemoveType.set("contract");
    this.templateRemoveConfirmDialogRef()?.open("contract");
  }

  /**
   * Downloads the static contract template example from /assets via a DOM anchor.
   */
  protected downloadContractTemplateExample(): void {
    this._downloadTemplateExample("contract");
  }

  // ==========================================================================
  // Custom document templates — shared helpers
  // ==========================================================================

  /**
   * Generic handler for opening a .docx file picker and routing the selected
   * file to the upload pipeline for the given template type.
   */
  private _triggerTemplateUpload(templateType: CustomTemplateType): void {
    const input = document.createElement("input");
    input.type = "file";
    input.accept =
      ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    input.onchange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        this._uploadTemplate(file, templateType);
      }
    };
    input.click();
  }

  /**
   * Entry point of the upload flow: validates the uploaded `.docx` and decides
   * what to do next based on the validation result.
   *
   *  - If syntax is invalid → opens the structured errors dialog (no upload).
   *  - If syntax is valid but some canonical placeholders are missing →
   *    pauses the flow and opens the warnings dialog. The user can confirm
   *    ("Carica comunque" → resumes via {@link onWarningConfirmed}) or cancel.
   *  - If syntax is valid and no placeholders are missing → uploads immediately.
   *
   * On non-validation errors (S3 upload failures, backend PATCH errors) it
   * surfaces a toast error from {@link _performUpload}.
   */
  private _uploadTemplate(
    file: File,
    templateType: CustomTemplateType,
  ): void {
    const currentTenant = this.tenant();
    if (!currentTenant?.id || !currentTenant?.name) {
      this._toastService.error("Tenant non trovato");
      return;
    }

    this._customTemplateService
      .validateTemplate(file, templateType)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (result) => {
          if (result.missingPlaceholders.length > 0) {
            // Pin the pending upload and surface the warning dialog. The
            // upload only resumes on "Carica comunque" via onWarningConfirmed.
            const allPlaceholders =
              this._customTemplateService.getCanonicalPlaceholders(
                templateType,
              );
            const missingMetadata: TemplatePlaceholderMetadata[] =
              result.missingPlaceholders
                .map((key) => allPlaceholders.find((p) => p.key === key))
                .filter(
                  (p): p is TemplatePlaceholderMetadata => p !== undefined,
                );
            this._pendingUpload.set({ file, templateType });
            this.templateValidationWarningsDialogRef()?.open(
              missingMetadata,
              templateType,
            );
          } else {
            this._performUpload(file, templateType);
          }
        },
        error: (err: unknown) => {
          if (this._isValidationFailure(err)) {
            this.templateValidationDialogRef()?.open(
              err.errors,
              templateType,
            );
          } else {
            const message =
              err instanceof Error && err.message
                ? err.message
                : "Errore durante la convalida del modello.";
            this._toastService.error(message);
          }
        },
      });
  }

  /**
   * Performs the actual upload pipeline (presigned S3 POST + tenant PATCH +
   * signal sync). Called either directly by `_uploadTemplate` when validation
   * passes cleanly, or by `onWarningConfirmed` after the user accepts the
   * missing-placeholders warning.
   */
  private _performUpload(
    file: File,
    templateType: CustomTemplateType,
  ): void {
    const currentTenant = this.tenant();
    if (!currentTenant?.id || !currentTenant?.name) {
      this._toastService.error("Tenant non trovato");
      return;
    }

    const fieldName = TEMPLATE_S3_KEY_FIELD[templateType];
    const enFieldName = TEMPLATE_EN_S3_KEY_FIELD[templateType];

    const suppressToast = new HttpContext().set(SUPPRESS_SUCCESS_TOAST, true);

    this._customTemplateService
      .uploadTemplate(file, templateType, currentTenant.name, suppressToast)
      .pipe(
        // Generate the English version of the freshly uploaded template.
        // A failure (translation error or mangled placeholders) is not
        // blocking: the Italian template is saved anyway and English
        // downloads fall back to the default template.
        switchMap((s3Key) =>
          this._customTemplateService
            .translateTemplateToEnglish(currentTenant.name, s3Key, templateType)
            .pipe(map((enS3Key) => ({ s3Key, enS3Key }))),
        ),
        // Persist both keys atomically so the tenant always points to a
        // coherent IT/EN pair of the same template version.
        switchMap(({ s3Key, enS3Key }) =>
          this._tenantsService
            .tenantsControllerUpdate(
              {
                id: currentTenant.id,
                body: { [fieldName]: s3Key, [enFieldName]: enS3Key },
              },
              suppressToast,
            )
            .pipe(map((updatedTenant) => ({ updatedTenant, enS3Key }))),
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: ({ updatedTenant, enS3Key }) => {
          this._loggedUserService.updateTenant(updatedTenant);
          if (enS3Key) {
            this._toastService.success(
              "Modello caricato",
              "Il modello personalizzato è stato salvato, insieme alla sua versione inglese generata automaticamente.",
            );
          } else {
            this._toastService.warning(
              "Modello salvato, ma la versione inglese non è stata generata: i download in inglese useranno il template di default.",
            );
          }
        },
        error: (err: unknown) => {
          const message =
            err instanceof Error && err.message
              ? err.message
              : "Errore durante il caricamento del modello.";
          this._toastService.error(message);
        },
      });
  }

  /**
   * Type guard for `CustomTemplateValidationFailure` — true when the error
   * comes from `CustomTemplateService.validateTemplate()` and carries a
   * structured `errors` array.
   */
  private _isValidationFailure(
    err: unknown,
  ): err is CustomTemplateValidationFailure {
    return (
      typeof err === "object" &&
      err !== null &&
      "errors" in err &&
      Array.isArray((err as CustomTemplateValidationFailure).errors)
    );
  }

  /**
   * Patches the tenant with `null` for the corresponding template field,
   * triggering a `$unset` on the server side. The S3 file is intentionally
   * left orphan.
   */
  private _removeTemplate(templateType: CustomTemplateType): void {
    const currentTenant = this.tenant();
    if (!currentTenant?.id) {
      this._toastService.error("Tenant non trovato");
      return;
    }

    const fieldName = TEMPLATE_S3_KEY_FIELD[templateType];
    const enFieldName = TEMPLATE_EN_S3_KEY_FIELD[templateType];

    this._tenantsService
      .tenantsControllerUpdate({
        id: currentTenant.id,
        body: { [fieldName]: null, [enFieldName]: null },
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (updatedTenant) => {
          this._loggedUserService.updateTenant(updatedTenant);
          this._toastService.success("Template rimosso");
        },
        error: () =>
          this._toastService.error(
            "Errore durante la rimozione del template.",
          ),
      });
  }

  /**
   * Triggers a browser download of the static example `.docx` bundled under
   * `/assets/templates/`.
   */
  private _downloadTemplateExample(templateType: CustomTemplateType): void {
    const { fileName, downloadName } = TEMPLATE_EXAMPLE_FILE[templateType];

    const link = document.createElement("a");
    link.href = `assets/templates/${fileName}`;
    link.download = downloadName;
    link.click();
  }
}
